import Path from 'webmango-utils/path'

import { createSelector } from 'reselect'

import * as File from './file'
import * as Datatype from './datatype'
import * as Process from './process'
import * as Schema from './schema'
import * as Sample from './sample'
import { get as getNode } from './node'
import {
  getAll as getStatuses,
  get as getStatus
} from './status'

import * as selfExports from './runsection'

import { getArgN, makeAPI } from './utils'

import { EXPIRY } from './expiry'

export const fromInputData = createSelector([ File.getAPI, Sample.getForProcess, getNode, getStatus ], (FileAPI, sample, node, status) => {
  if (!FileAPI) throw new Error('state required')
  if (!sample) throw new Error('sample required')
  if (!node) throw new Error('node required')

  const file = Path.resolve(sample.localSamplePath, node.value['inputdata/filename'])
  return Object.assign({}, {
    // RunSection
    input_data_type: FileAPI.getDatatype(file),
    input_file_name: FileAPI.getFileName(file),
    input_extension: FileAPI.getExtension(file),
    get output_file_name () {
      return this.input_file_name
    },
    get output_extension () {
      return this.input_extension
    },
    suffix: FileAPI.getSuffix(file),
    enabled: status.enabled
  })
})

export const fromNode = createSelector([
  File.getAPI,
  Datatype.getAPI,
  Schema.getForNode,
  getNode,
  getStatus,
  getArgN(3)
], (FileAPI, DatatypeAPI, schema, node, status, inputRunSection) => {
  if (!FileAPI) throw new Error('FileAPI required')
  if (!DatatypeAPI) throw new Error('FileAPI required')
  if (!schema) throw new Error('schema required')
  if (!node) throw new Error('node required')
  if (!status) throw new Error(`status required - ${node.id}`)
  if (!inputRunSection) throw new Error(`inputRunSection required - ${node.id}`)

  const inputDatatype = inputRunSection.output_file_name ? FileAPI.getDatatype(inputRunSection.output_file_name) : ''

  let outputDatatype = schema.types[inputDatatype] || ''
  if (outputDatatype === true || schema.type === 'inplace') {
    outputDatatype = inputDatatype
  }

  return Object.assign({}, {
    input_data_type: inputDatatype, // derive from input file name
    input_file_name: inputRunSection.output_file_name, // copy from output of input
    input_extension: inputRunSection.output_extension, // copy from output of input
    get output_file_name () {
      if (!outputDatatype) return ''
      // analysis nodes just copy output from input
      if (schema.type === 'analysis') return this.input_file_name

      // datatype's base + current constructed suffix + run_identifier
      const suffix = schema.suffix ? `_${schema.suffix}` : ''
      return [
        DatatypeAPI.getBaseFromDatatype(outputDatatype),
        this.suffix,
        suffix,
        this.run_identifier
      ].join('')
    },
    get output_extension () {
      if (schema.name === 'Import_Data_File') {
        return 'nc'
      } else {
        return inputRunSection.input_extension // extension follows input
      }
    },
    get suffix () {
      // suffix comes from input file name
      return FileAPI.getSuffix(inputRunSection.output_file_name)
    },
    enabled: status.enabled,
    run_identifier: status.runID || '',
    get time_to_keep () {
      if (schema.type === 'analysis') return undefined
      const defaultIndex = status.isRunEnd ? EXPIRY.defaultLast : EXPIRY.default
      const defaultExpiry = EXPIRY.times[defaultIndex]
      let expiry = node.expiry
      if (!expiry || expiry === 'Default') {
        expiry = defaultExpiry
      }
      return expiry
    }
  })
})

export const getAPI = makeAPI(selfExports, [
  File.getAPI,
  Datatype.getAPI,
  Schema.getAPI,
  Process.getAll
])

export const getAll = createSelector([ getAPI, getStatuses, Process.get ], (RunSectionAPI, statuses, process) => {
  if (!statuses) throw new Error('statuses required')
  if (!process) throw new Error('process required')

  const validNodes = process.nodes.filter(node => {
    const status = statuses[node.id]
    return !status.ignored && status.isValid
  })

  if (!validNodes.length) return {}

  const inputNode = validNodes[0]
  const runSections = {
    [inputNode.id]: RunSectionAPI.fromInputData(process.id, inputNode.id)
  }

  validNodes.slice(1).forEach(node => {
    const status = statuses[node.id]
    runSections[node.id] = RunSectionAPI.fromNode(process.id, node.id, runSections[status.inputNodeID])
  })

  return runSections
})

export const get = createSelector([ getAll, getArgN(2) ], (runSections, nodeID) => {
  return runSections[nodeID]
})



// WEBPACK FOOTER //
// ../webmango-core/selectors/runsection.js